import { LOCAL_STORAGE } from "../globals/globals";

export const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift();
        return cookieValue || null;
    }
    return null;
};

/**
 * Returns the base domain the app is served from, stripping any leading
 * `proxy-<id>` device-proxy subdomain. Returns an empty string for bare IPs and
 * localhost, where cookie `domain` scoping does not apply and subdomain proxying
 * is not available.
 */
export const getBaseDomain = (): string => {
    const host = window.location.hostname;
    if (!host || host === "localhost") return "";
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(":")) return "";
    return host.startsWith("proxy-") ? host.split(".").slice(1).join(".") : host;
};

/**
 * Writes a cookie scoped to the base domain (`.<base-domain>`) so it is sent to
 * all `proxy-*` device subdomains. Falls back to a host-only cookie when there
 * is no usable base domain (IP/localhost).
 */
export const setCookie = (name: string, value: string, maxAgeSeconds?: number): void => {
    const baseDomain = getBaseDomain();
    const secure = window.location.protocol === "https:" ? "; secure" : "";
    const maxAge = maxAgeSeconds ? `; max-age=${maxAgeSeconds}` : "";
    const domain = baseDomain ? `; domain=.${baseDomain}` : "";
    document.cookie = `${name}=${value}; path=/${domain}${maxAge}; samesite=lax${secure}`;
};

/**
 * Deletes a cookie both with and without the base-domain attribute so it is
 * reliably cleared from the app host and every proxy subdomain.
 */
export const deleteCookie = (name: string): void => {
    const baseDomain = getBaseDomain();
    const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `${name}=; path=/; ${expired}`;
    if (baseDomain) {
        document.cookie = `${name}=; path=/; domain=.${baseDomain}; ${expired}`;
    }
};

export const getLastSSHActivity = (): number | null => {
    try {
        const lastActivity = getCookie(LOCAL_STORAGE.SSHACTIVITYCOOKIE);
        if (!lastActivity) {
            return null;
        }

        const timestamp = parseInt(lastActivity, 10);
        return isNaN(timestamp) ? null : timestamp;
    } catch (error) {
        console.warn('Error reading SSH activity from cookie:', error);
        return null;
    }
};